import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Building2, Trash2, Globe, Settings, Edit, Eye, MoreHorizontal } from "lucide-react"
import axiosInstance from "../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../constants/config"
import EditSchoolModal from "./EditSchoolModal"
import Pagination from "../Common/Pagination"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

function SchoolList({ schools, onDeleteSchool, onSettingsUpdate }) {
  const navigate = useNavigate()
  const [selectedSchool, setSelectedSchool] = useState(null)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [togglingCronId, setTogglingCronId] = useState(null)
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}")
    } catch {
      return {}
    }
  })()
  const canToggleStatus = user?.role === "superadmin"

  const handleToggleAutoAbsentCron = async (school) => {
    if (!canToggleStatus) return
    const id = school._id?.toString?.() ?? school._id
    if (!id) return
    const nextEnabled = !(school.autoAbsentCronEnabled !== false)
    setTogglingCronId(id)
    try {
      const token = localStorage.getItem("token")
      const res = await axiosInstance.patch(
        API_ENDPOINTS.school(id),
        { autoAbsentCronEnabled: nextEnabled },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (res.data?.status === "success" && res.data.data?.school) {
        const updatedSchools = schools.map((s) => (s._id === id || s._id?.toString?.() === id ? res.data.data.school : s))
        onSettingsUpdate?.(updatedSchools)
      }
    } catch (error) {
      console.error("Failed to toggle auto-absent cron:", error)
      window.alert(error?.response?.data?.message || "Failed to update auto-absent cron status")
    } finally {
      setTogglingCronId(null)
    }
  }

  const handleSettingsUpdate = (updatedSchool) => {
    const updatedSchools = schools.map(school => 
      school._id === updatedSchool._id ? updatedSchool : school
    )
    onSettingsUpdate?.(updatedSchools)
  }

  const paginatedSchools = schools.slice((page - 1) * itemsPerPage, page * itemsPerPage)

  if (!schools.length) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center text-gray-500">
        <Building2 className="h-16 w-16 mx-auto mb-3 text-blue-300" />
        <p>No customers have been added yet.</p>
      </div>
    )
  }

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Customer</th>
                <th className="px-4 py-3 text-left font-semibold">Type</th>
                <th className="px-4 py-3 text-left font-semibold">Domain</th>
                <th className="px-4 py-3 text-right font-semibold">Staff</th>
                <th className="px-4 py-3 text-right font-semibold">Total Users</th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-center font-semibold">Auto-Absent Cron</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedSchools.map((school) => (
                <tr
                  key={school._id}
                  onClick={() => navigate(`/dashboard/schools/${school._id?.toString?.() ?? school._id}`)}
                  className="cursor-pointer hover:bg-gray-50/60"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-gray-900 font-medium">
                      <Building2 className="h-4 w-4 text-blue-600" />
                      {school.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 capitalize">
                    {school.tenantType || "school"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-gray-400" />
                      {school.domain}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700">{school.stats?.totalStaff ?? 0}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-gray-900">
                    {school.stats?.totalUsers ?? school.userCount ?? 0}
                  </td>
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <span
                      title="Change this from the customer's Billing tab"
                      className={`rounded-full px-2.5 py-1 text-xs font-medium border ${
                        school.isActive !== false
                          ? "border-green-300 bg-green-50 text-green-700"
                          : "border-gray-300 bg-gray-100 text-gray-600"
                      }`}
                    >
                      {school.isActive !== false ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    {canToggleStatus ? (
                      <button
                        type="button"
                        onClick={() => handleToggleAutoAbsentCron(school)}
                        disabled={togglingCronId === (school._id?.toString?.() ?? school._id)}
                        className={`rounded-full px-2.5 py-1 text-xs font-medium border transition-colors ${
                          school.autoAbsentCronEnabled !== false
                            ? "border-green-300 bg-green-50 text-green-700"
                            : "border-gray-300 bg-gray-100 text-gray-600"
                        }`}
                        title={school.autoAbsentCronEnabled !== false ? "Click to turn off auto-absent cron for this customer" : "Click to turn on auto-absent cron for this customer"}
                      >
                        {togglingCronId === (school._id?.toString?.() ?? school._id)
                          ? "..."
                          : school.autoAbsentCronEnabled !== false
                            ? "Active"
                            : "Inactive"}
                      </button>
                    ) : (
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium border ${
                          school.autoAbsentCronEnabled !== false
                            ? "border-green-300 bg-green-50 text-green-700"
                            : "border-gray-300 bg-gray-100 text-gray-600"
                        }`}
                      >
                        {school.autoAbsentCronEnabled !== false ? "Active" : "Inactive"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open actions</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => navigate(`/dashboard/schools/${school._id?.toString?.() ?? school._id}`)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          View details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedSchool(school)
                            setIsEditOpen(true)
                          }}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate(`/dashboard/schools/${school._id?.toString?.() ?? school._id}/settings`)}
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          Settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onDeleteSchool(school._id?.toString?.() ?? school._id)}
                          className="text-red-600 focus:text-red-600 focus:bg-red-50"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={page}
          totalItems={schools.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setPage}
          onItemsPerPageChange={(newLimit) => { setItemsPerPage(newLimit); setPage(1); }}
          pageSizeOptions={[5, 10, 25, 50]}
          itemLabel="customers"
        />
      </div>

      <EditSchoolModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        school={selectedSchool}
        onSchoolUpdated={(updatedSchool) => {
          const updatedSchools = schools.map((s) =>
            s._id === updatedSchool._id ? updatedSchool : s
          )
          onSettingsUpdate?.(updatedSchools)
        }}
      />
    </>
  )
}

export default SchoolList